import type { UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import type { SolarProposalDto } from "../dto/solar-proposal.dto"
import { solarProposalService } from "../services/solar-proposal.service"

export const SUMMARY_NOT_CONFIRMED = `The server did not record the Summary as confirmed.`

/**
 * Confirms a Solar Proposal's Summary. The server checks the requirements and
 * records the confirmation; the proposal it answers is the authority on
 * whether the Summary gate is open.
 */
export default function useConfirmSolarProposalSummary(): UseMutationResult<
  SolarProposalDto,
  unknown,
  { proposalId: string }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ proposalId }) => {
      const proposal = await solarProposalService.postSummaryConfirm(proposalId)

      // A 200 without the timestamp is not a confirmation: kept out of the
      // cache, it can't leave the seller on a Summary that reports no error.
      if (proposal.summaryConfirmedAt == null) {
        throw new Error(SUMMARY_NOT_CONFIRMED)
      }

      return proposal
    },
    onSuccess: async (proposal, { proposalId }) => {
      // Savings, run a moment ago, invalidated the header; a refetch started
      // before the confirm committed would land last and bounce the seller
      // back into the Summary.
      await queryClient.cancelQueries({
        queryKey: queryKeys.solarProposal.byId(proposalId),
        exact: true,
      })
      queryClient.setQueryData(
        queryKeys.solarProposal.byId(proposalId),
        proposal,
      )
      void queryClient.invalidateQueries({
        queryKey: queryKeys.solarProposal.summary(proposalId),
        exact: true,
      })
    },
    // A 400 names what is missing: the readiness read shows it step by step.
    onError: (_error, { proposalId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.solarProposal.summary(proposalId),
        exact: true,
      })
    },
  })
}
